import { useState, useEffect, useRef, Fragment } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";
import ListingEditModal from "../components/ListingEditModal";
import NeighborhoodSelect from "../components/NeighborhoodSelect";
import BuildingDetailPanel from "../components/BuildingDetailPanel";
import ImageReviewModal from "../components/ImageReviewModal";
import ListingMergeModal from "../components/ListingMergeModal";

// Verification-gate reasons -> short chip labels for the Review tab "Needs" column.
const REASON_LABELS = {
  beds_baths_mismatch: "beds/baths",
  floor_missing: "floor",
  floor_line_missing: "floor line",
  sqft_missing: "sqft",
  hoa_missing: "HOA",
  tax_missing: "tax",
  reno_no_description: "reno",
  reno_high_no_dw: "reno+DW",
  no_se_source: "SE link",
  whole_building_unit: "whole-bldg",
};

const BEDROOM_OPTIONS = [null, 0, 1, 2, 3, 4, 5];
const BATHROOM_OPTIONS = [null, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
const YES_NO_UNKNOWN = [
  { value: null, label: "-" },
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];
const WASHER_DRYER_OPTIONS = [
  { value: null, label: "-" },
  { value: "Y", label: "Yes" },
  { value: "N", label: "No" },
  { value: "U", label: "Unknown" },
];
const RENOVATION_OPTIONS = [
  { value: null, label: "-" },
  { value: "1", label: "1 - Low" },
  { value: "2", label: "2 - Mid-low" },
  { value: "3", label: "3 - Mid" },
  { value: "4", label: "4 - Mid-high" },
  { value: "5", label: "5 - High" },
];
const VIEWS_OPTIONS = [
  { value: null, label: "-" },
  { value: "0", label: "No" },
  { value: "1", label: "Premium" },
];
const STATUS_OPTIONS = [
  "ACTIVE", "APP ACCEPTED", "APP IN", "APP IN THRU US",
  "BACK ON MKT", "BOARD APPROVED", "CLOSED", "CLOSED THRU US",
  "COMING SOON", "CONTRACT OUT", "CONTRACT SIGNED",
  "LSE OUT", "LSE SIGN", "OFF EXCLUSIVE", "OFFER ACCEPTED",
  "PERM OFF MKT", "RENTED", "RENTED THRU US", "TEMP OFF MKT",
  "DELETE", "DELETED", "DELISTED", "FUTURE", "NOT RENTED", "SOLD",
];

export default function ListingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // Initialize search from URL params immediately to avoid race condition
  const [search, setSearch] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("search") || "";
  });
  const [searchInput, setSearchInput] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("search") || "";
  });
  const [saleOrRental, setSaleOrRental] = useState("");
  const [ownershipType, setOwnershipType] = useState("");
  const [sortField, setSortField] = useState("created_date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [editModalListing, setEditModalListing] = useState(null); // listing object for modal
  const [imageReviewListing, setImageReviewListing] = useState(null);
  const [enrichingId, setEnrichingId] = useState(null);
  const [lastEnrichedId, setLastEnrichedId] = useState(null);
  const [seSearchingId, setSeSearchingId] = useState(null);
  const [seManualUrlId, setSeManualUrlId] = useState(null); // listing id showing manual URL input
  const [seManualUrl, setSeManualUrl] = useState("");
  const [renoSuggestion, setRenoSuggestion] = useState(null); // { listingId, current, suggested, reasoning }
  const [showAddModal, setShowAddModal] = useState(false);
  const [addRpId, setAddRpId] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState("");
  const [addSuccess, setAddSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("unverified");
  const [statusFilter, setStatusFilter] = useState("active");
  const [verifyingId, setVerifyingId] = useState(null);
  const [showCoopModal, setShowCoopModal] = useState(null); // listing object or null
  const [mergeListingId, setMergeListingId] = useState(null); // listing id for merge modal
  const [coopForm, setCoopForm] = useState({
    sublet_policy: "",
    sublet_vesting_details: "",
    sublet_sometimes_numerator: "",
    sublet_sometimes_denominator: "",
    international_buyers: "",
    llc_buyer: "",
    board_interview_rentals: "",
    board_interview_sales: "",
    special_notes: "",
  });
  const [coopSaving, setCoopSaving] = useState(false);
  const [buildingDetail, setBuildingDetail] = useState(null);
  const [buildingDetailLoading, setBuildingDetailLoading] = useState(false);
  const editRowRef = useRef(null);
  const [taxNotePopover, setTaxNotePopover] = useState(null); // { id: listingId, x, y } for positioning
  const [taxNoteHover, setTaxNoteHover] = useState(null); // listingId for hover tooltip
  const [limit, setLimit] = useState(20);
  // Initialize filterListingId from URL params immediately to avoid race condition
  const [filterListingId, setFilterListingId] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const listingIdFromUrl = urlParams.get("listing_id");
    return listingIdFromUrl ? parseInt(listingIdFromUrl) : null;
  });

  // Clear URL parameters after initial load (values already read via useState initializers)
  useEffect(() => {
    const hasParams = searchParams.get("search") || searchParams.get("listing_id");
    if (hasParams) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, filterListingId, saleOrRental, ownershipType, selectedNeighborhood, sortField, sortDirection, activeTab, statusFilter]);

  // Load neighborhoods for filter dropdown
  useEffect(() => {
    authFetch("/sale-reviews/neighborhoods")
      .then((data) => setNeighborhoods(data.neighborhoods || []))
      .catch((err) => console.error("Error loading neighborhoods:", err));
  }, []);

  // Close edit mode on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && editingId) {
        setEditingId(null);
        setEditData({});
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [editingId]);

  const loadListings = async ({ preserveScroll = false } = {}) => {
    const scrollY = preserveScroll ? window.scrollY : 0;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (filterListingId) params.append("listing_id", filterListingId);
      if (search) params.append("search", search);
      if (saleOrRental) params.append("sale_or_rental", saleOrRental);
      if (ownershipType) params.append("ownership_type", ownershipType);
      if (activeTab === "excluded") {
        params.append("tab", "excluded");
      } else if (activeTab === "duplicates") {
        params.append("tab", "duplicates");
      } else if (activeTab === "townhouses") {
        params.append("tab", "townhouses");
      } else {
        params.append("verified", activeTab === "verified" ? "true" : "false");
      }
      if (selectedNeighborhood) params.append("neighborhood_ids", selectedNeighborhood);
      if (statusFilter && statusFilter !== "active") params.append("status_filter", statusFilter);
      if (sortField) {
        params.append("sort_by", sortField);
        params.append("sort_dir", sortDirection);
      }
      const response = await authFetch(`/listings/data-quality?${params}`);
      setListings(response.listings);
      setTotal(response.total);
      if (preserveScroll) {
        requestAnimationFrame(() => window.scrollTo(0, scrollY));
      }
    } catch (error) {
      console.error("Error loading listings:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadBuildingDetail = async (buildingId) => {
    setBuildingDetailLoading(true);
    try {
      const response = await authFetch(`/buildings/${buildingId}`);
      setBuildingDetail(response);
    } catch (error) {
      console.error("Error loading building detail:", error);
    } finally {
      setBuildingDetailLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const startEditing = (listing) => {
    if (editingId === listing.id) return;
    setEditingId(listing.id);
    setEditData({
      unit_number: listing.unit_number || "",
      floor: listing.floor || "",
      bedrooms: listing.bedrooms,
      bathrooms: listing.bathrooms,
      sqft: listing.sqft,
      list_price: listing.list_price,
      listing_status: listing.listing_status,
      dishwasher: listing.dishwasher,
      washer_dryer: listing.washer_dryer,
      renovation_level: listing.renovation_level,
      premium_views: listing.premium_views,
      hoa: listing.hoa,
      tax: listing.tax,
      floor_line: listing.floor_line || "",
      listing_url: listing.listing_url || "",
      block: listing.block || "",
      lot: listing.lot || "",
      borough: listing.borough || "",
      acris_url: listing.acris_url || "",
      tax_abatement: listing.tax_abatement || false,
      tax_abatement_description: listing.tax_abatement_description || "",
      // Abatement detail fields live in listing_details (JSONB) — mirror the modal
      tax_abatement_type: (listing.listing_details || {}).tax_abatement_type || "",
      tax_abatement_type_other: (listing.listing_details || {}).tax_abatement_type_other || "",
      tax_abatement_verified_date: (listing.listing_details || {}).tax_abatement_verified_date || "",
      tax_abatement_with: (listing.listing_details || {}).tax_abatement_with || "",
      tax_abatement_without: (listing.listing_details || {}).tax_abatement_without || "",
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditData({});
    setTaxNotePopover(null);
  };

  const saveEditing = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      // Build payload - only send fields that changed
      const original = listings.find((l) => l.id === editingId);
      const payload = {};

      if (editData.unit_number !== (original.unit_number || ""))
        payload.unit_number = editData.unit_number || null;
      if (editData.floor !== (original.floor || ""))
        payload.floor = editData.floor || null;
      if (editData.bedrooms !== original.bedrooms)
        payload.bedrooms = editData.bedrooms;
      if (editData.bathrooms !== original.bathrooms)
        payload.bathrooms = editData.bathrooms;
      if (editData.sqft !== original.sqft)
        payload.sqft = editData.sqft ? parseInt(editData.sqft) : null;
      if (editData.list_price !== original.list_price)
        payload.list_price = editData.list_price ? parseFloat(editData.list_price) : null;
      if (editData.listing_status !== original.listing_status)
        payload.listing_status = editData.listing_status;
      if (editData.dishwasher !== original.dishwasher)
        payload.dishwasher = editData.dishwasher;
      if (editData.washer_dryer !== original.washer_dryer)
        payload.washer_dryer = editData.washer_dryer;
      if (editData.renovation_level !== original.renovation_level)
        payload.renovation_level = editData.renovation_level;
      if (editData.premium_views !== original.premium_views)
        payload.premium_views = editData.premium_views;
      if (editData.hoa !== original.hoa)
        payload.hoa = editData.hoa ? parseFloat(editData.hoa) : null;
      if (editData.tax !== original.tax)
        payload.tax = editData.tax ? parseFloat(editData.tax) : null;
      if (editData.floor_line !== (original.floor_line || ""))
        payload.floor_line = editData.floor_line || null;
      if (editData.listing_url !== (original.listing_url || ""))
        payload.listing_url = editData.listing_url || null;
      if (editData.block !== (original.block || ""))
        payload.block = editData.block || null;
      if (editData.lot !== (original.lot || ""))
        payload.lot = editData.lot || null;
      if (editData.borough !== (original.borough || ""))
        payload.borough = editData.borough || null;
      if (editData.acris_url !== (original.acris_url || ""))
        payload.acris_url = editData.acris_url || null;
      if (editData.tax_abatement !== (original.tax_abatement || false))
        payload.tax_abatement = editData.tax_abatement;
      if (editData.tax_abatement_description !== (original.tax_abatement_description || ""))
        payload.tax_abatement_description = editData.tax_abatement_description || null;

      // Abatement detail fields -> listing_details (JSONB, merged server-side)
      const ld = original.listing_details || {};
      const details = {};
      if ((editData.tax_abatement_type || "") !== (ld.tax_abatement_type || ""))
        details.tax_abatement_type = editData.tax_abatement_type || null;
      if ((editData.tax_abatement_type_other || "") !== (ld.tax_abatement_type_other || ""))
        details.tax_abatement_type_other = editData.tax_abatement_type_other || null;
      if ((editData.tax_abatement_verified_date || "") !== (ld.tax_abatement_verified_date || ""))
        details.tax_abatement_verified_date = editData.tax_abatement_verified_date || null;
      if (String(editData.tax_abatement_with || "") !== String(ld.tax_abatement_with || ""))
        details.tax_abatement_with = editData.tax_abatement_with ? parseFloat(editData.tax_abatement_with) : null;
      if (String(editData.tax_abatement_without || "") !== String(ld.tax_abatement_without || ""))
        details.tax_abatement_without = editData.tax_abatement_without ? parseFloat(editData.tax_abatement_without) : null;
      if (Object.keys(details).length > 0) payload.listing_details = details;

      if (Object.keys(payload).length === 0) {
        cancelEditing();
        return;
      }

      const saveResult = await authFetch(`/listings/${editingId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });

      // If the human's fix satisfied the gate, the listing auto-verified and leaves the
      // Review tab — drop it from the list (the fast loop).
      if (saveResult?.verified) {
        const fixedId = editingId;
        setListings((prev) => prev.filter((l) => l.id !== fixedId));
        setEditingId(null);
        setEditData({});
        setTaxNotePopover(null);
        return;
      }

      // Otherwise update the local listing data (still needs more / still flagged)
      setListings((prev) =>
        prev.map((l) => {
          if (l.id !== editingId) return l;
          return {
            ...l,
            unit_number: editData.unit_number || null,
            floor: editData.floor || null,
            bedrooms: editData.bedrooms,
            bathrooms: editData.bathrooms,
            sqft: editData.sqft ? parseInt(editData.sqft) : null,
            list_price: editData.list_price ? parseFloat(editData.list_price) : null,
            listing_status: editData.listing_status,
            dishwasher: editData.dishwasher,
            washer_dryer: editData.washer_dryer,
            renovation_level: editData.renovation_level,
            premium_views: editData.premium_views,
            hoa: editData.hoa ? parseFloat(editData.hoa) : null,
            tax: editData.tax ? parseFloat(editData.tax) : null,
            floor_line: editData.floor_line || null,
            listing_url: editData.listing_url || null,
            block: editData.block || null,
            lot: editData.lot || null,
            borough: editData.borough || null,
            acris_url: editData.acris_url || null,
            tax_abatement: editData.tax_abatement,
            tax_abatement_description: editData.tax_abatement_description || null,
            // Refresh the gate chips from the re-gate result (so resolved chips disappear)
            review_reasons: saveResult?.review_reasons ?? l.review_reasons,
            // Clear source indicators since user manually set values
            dishwasher_source: payload.dishwasher !== undefined ? "actual" : l.dishwasher_source,
            washer_dryer_source: payload.washer_dryer !== undefined ? "actual" : l.washer_dryer_source,
            floor_line_source: payload.floor_line !== undefined ? "actual" : l.floor_line_source,
          };
        })
      );
      setEditingId(null);
      setEditData({});
      setTaxNotePopover(null);
    } catch (error) {
      console.error("Error saving listing:", error);
      alert("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const getSeStatus = (listing) => {
    if (listing.se_updated_at) return "enriched"; // green
    if (listing.listing_url && listing.listing_url.toLowerCase().includes("streeteasy.com")) return "has_url"; // blue
    return "no_url"; // gray
  };

  const checkRenoSuggestion = (result, listing) => {
    const reno = result.reno_suggestion;
    if (reno && reno.success && reno.changed) {
      setRenoSuggestion({
        listingId: listing.id,
        address: listing.address || `Listing #${listing.id}`,
        current: reno.current_renovation,
        suggested: reno.suggested_renovation,
        reasoning: reno.reasoning,
        images: result.se_images || [],
        description: result.se_description || "",
        expanded: false,
      });
    }
  };

  const acceptRenoSuggestion = async () => {
    if (!renoSuggestion) return;
    try {
      await authFetch(`/listings/${renoSuggestion.listingId}`, {
        method: "PATCH",
        body: JSON.stringify({ renovation_level: renoSuggestion.suggested }),
      });
      await loadListings({ preserveScroll: true });
    } catch (error) {
      console.error("Failed to update reno:", error);
    }
    setRenoSuggestion(null);
  };

  const handleSeClick = async (listing) => {
    const status = getSeStatus(listing);
    if (enrichingId || seSearchingId) return;

    if (status === "has_url" || status === "enriched") {
      // Has SE URL → enrich directly
      setEnrichingId(listing.id);
      try {
        const result = await authFetch(`/listings/${listing.id}/enrich-streeteasy`, {
          method: "POST",
          body: JSON.stringify({
            se_url: listing.listing_url,
            sale_or_rental: listing.sale_or_rental,
          }),
        });
        if (result.success) {
          checkRenoSuggestion(result, listing);
          setLastEnrichedId(listing.id);
          await loadListings({ preserveScroll: true });
        } else {
          // Enrichment failed → show manual URL input so user can try a different URL
          setSeManualUrlId(listing.id);
          setSeManualUrl(listing.listing_url || "");
        }
      } catch (error) {
        console.error("SE enrichment error:", error);
        // Show manual URL input on failure
        setSeManualUrlId(listing.id);
        setSeManualUrl(listing.listing_url || "");
      } finally {
        setEnrichingId(null);
      }
    } else {
      // No SE URL → search via Serper
      setSeSearchingId(listing.id);
      try {
        const result = await authFetch(`/listings/${listing.id}/search-se-url`, {
          method: "POST",
        });
        if (result.found) {
          checkRenoSuggestion(result.enrichment || {}, listing);
          setLastEnrichedId(listing.id);
          await loadListings({ preserveScroll: true });
        } else {
          // Not found → show manual URL input
          setSeManualUrlId(listing.id);
          setSeManualUrl("");
        }
      } catch (error) {
        console.error("SE search error:", error);
        setSeManualUrlId(listing.id);
        setSeManualUrl("");
      } finally {
        setSeSearchingId(null);
      }
    }
  };

  const handleSeManualSubmit = async (listing) => {
    if (!seManualUrl || enrichingId) return;
    setEnrichingId(listing.id);
    try {
      const result = await authFetch(`/listings/${listing.id}/enrich-streeteasy`, {
        method: "POST",
        body: JSON.stringify({
          se_url: seManualUrl,
          sale_or_rental: listing.sale_or_rental,
        }),
      });
      if (result.success) {
        checkRenoSuggestion(result, listing);
        setSeManualUrlId(null);
        setSeManualUrl("");
        setLastEnrichedId(listing.id);
        await loadListings({ preserveScroll: true });
      } else {
        alert(result.error || "Enrichment failed");
      }
    } catch (error) {
      console.error("SE manual enrichment error:", error);
      alert("Failed to enrich from StreetEasy");
    } finally {
      setEnrichingId(null);
    }
  };

  const toggleVerify = async (listingId, currentStatus) => {
    if (verifyingId) return;
    setVerifyingId(listingId);
    try {
      // Toggle between TO_REVIEW and VERIFIED
      const newStatus = currentStatus === "VERIFIED" ? "TO_REVIEW" : "VERIFIED";
      const result = await authFetch(`/listings/data-quality/${listingId}/status?status=${newStatus}`, {
        method: "PATCH",
      });
      if (result.success) {
        setListings((prev) => prev.filter((l) => l.id !== listingId));
        setTotal((prev) => prev - 1);
      }
    } catch (error) {
      console.error("Verify error:", error);
      alert("Failed to update verification status");
    } finally {
      setVerifyingId(null);
    }
  };

  // Move a row to another internal_status (e.g. defer whole-building units to
  // TOWNHOUSE or soft-delete) and drop it from the current list.
  const setRowStatus = async (listingId, status) => {
    try {
      const result = await authFetch(`/listings/data-quality/${listingId}/status?status=${status}`, {
        method: "PATCH",
      });
      if (result?.success) {
        setListings((prev) => prev.filter((l) => l.id !== listingId));
        setTotal((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      alert("Failed to update status");
    }
  };

  const approveUnit = async (listingId) => {
    if (!window.confirm("Approve this unit? It will move to the To Review tab.")) return;
    try {
      const result = await authFetch(`/listings/data-quality/${listingId}/approve-unit`, { method: "PATCH" });
      if (result.success) {
        setListings((prev) => prev.filter((l) => l.id !== listingId));
        setTotal((prev) => prev - 1);
      }
    } catch (error) {
      console.error("Approve unit error:", error);
      alert("Failed to approve unit");
    }
  };

  const openCoopApproval = (listing) => {
    setShowCoopModal(listing);
    setCoopForm({
      sublet_policy: "",
      sublet_vesting_details: "",
      sublet_sometimes_numerator: "",
      sublet_sometimes_denominator: "",
      international_buyers: "",
      llc_buyer: "",
      board_interview_rentals: "",
      board_interview_sales: "",
      special_notes: "",
    });
    // Load existing policies if any
    if (listing.property_id) {
      authFetch(`/properties/${listing.property_id}/coop-policies`)
        .then((data) => {
          if (data.coop_policies) {
            setCoopForm((prev) => ({ ...prev, ...data.coop_policies }));
          }
        })
        .catch(() => {});
    }
  };

  const submitCoopApproval = async () => {
    if (!showCoopModal || !coopForm.sublet_policy) return;
    setCoopSaving(true);
    try {
      const result = await authFetch(`/listings/data-quality/${showCoopModal.id}/approve-building`, {
        method: "PATCH",
        body: JSON.stringify(coopForm),
      });
      if (result.success) {
        setShowCoopModal(null);
        await loadListings({ preserveScroll: true });
      }
    } catch (error) {
      console.error("Approve building error:", error);
      alert("Failed to approve building");
    } finally {
      setCoopSaving(false);
    }
  };

  const saveCoopPoliciesOnly = async () => {
    if (!showCoopModal) return;
    setCoopSaving(true);
    try {
      const result = await authFetch(`/listings/data-quality/${showCoopModal.id}/save-coop-policies`, {
        method: "PATCH",
        body: JSON.stringify(coopForm),
      });
      if (result.success) {
        setShowCoopModal(null);
      }
    } catch (error) {
      console.error("Save policies error:", error);
      alert("Failed to save policies");
    } finally {
      setCoopSaving(false);
    }
  };

  const addFromRealPlus = async () => {
    if (!addRpId || addLoading) return;
    setAddLoading(true);
    setAddError("");
    setAddSuccess("");
    try {
      const result = await authFetch("/listings/add-from-realplus", {
        method: "POST",
        body: JSON.stringify({ rp_id: parseInt(addRpId) }),
      });
      if (result.success) {
        setAddSuccess(`Listing ${result.status} (ID: ${result.listing_id})`);
        setAddRpId("");
        await loadListings({ preserveScroll: true });
      } else {
        setAddError(result.error || "Failed to add listing");
      }
    } catch (error) {
      console.error("Add from RealPlus error:", error);
      setAddError("Failed to add listing from RealPlus");
    } finally {
      setAddLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat("en-US").format(value);
  };

  const formatBoolean = (value, source) => {
    if (value === null || value === undefined) return "-";
    const display = value === true || value === "Y" || value === "Yes" ? "Yes" : value === false || value === "N" || value === "No" ? "No" : String(value);
    const isAI = source === "ai_high" || source === "ai_inferred" || source === "ai";
    const color = source === "ai_high" ? "#7b1fa2" : source === "ai_inferred" || source === "ai" ? "#64b5f6" : "inherit";
    return (
      <span style={{ color }}>
        {display}
        {isAI && <span style={{ fontSize: "10px", marginLeft: "2px" }}>*</span>}
      </span>
    );
  };

  const formatWithSource = (value, source) => {
    if (value === null || value === undefined || value === "") return "-";
    const isAI = source === "ai";
    return (
      <span style={{ color: isAI ? "#1976d2" : "inherit" }}>
        {value}
        {isAI && <span style={{ fontSize: "10px", marginLeft: "2px" }}>*</span>}
      </span>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return { bg: "#e8f5e9", color: "#2e7d32" };
      case "CONTRACT OUT":
      case "OFFER ACCEPTED":
        return { bg: "#fff3e0", color: "#e65100" };
      case "COMING SOON":
      case "FUTURE":
        return { bg: "#e3f2fd", color: "#1565c0" };
      default:
        return { bg: "#f5f5f5", color: "#666" };
    }
  };

  // Render an editable cell or a display cell
  const renderEditCell = (listing) => {
    const isEditing = editingId === listing.id;

    if (!isEditing) {
      return {
        unit: <td style={tdStyle}>{listing.unit_number || "-"}</td>,
        floor: <td style={tdStyle}>{listing.floor || "-"}</td>,
        beds: <td style={tdStyle}>{listing.bedrooms === null || listing.bedrooms === undefined ? "-" : listing.bedrooms === 0 ? "Studio" : listing.bedrooms}</td>,
        baths: <td style={tdStyle}>{listing.bathrooms ?? "-"}</td>,
        sqft: (() => {
          const verifiedSqft = listing.listing_details?.sqft_verified;
          const displaySqft = verifiedSqft || listing.sqft;
          return (
            <td style={tdStyle}>
              {displaySqft ? (
                <span style={{ color: verifiedSqft ? "#2e7d32" : listing.sqft_is_estimated ? "#e65100" : "inherit", fontWeight: verifiedSqft ? 600 : "normal" }}>
                  {formatNumber(displaySqft)}
                  {verifiedSqft ? <span style={{ fontSize: "10px", marginLeft: "2px" }}>✓</span> : listing.sqft_is_estimated ? <span style={{ fontSize: "10px", marginLeft: "2px" }}>~</span> : null}
                </span>
              ) : "-"}
            </td>
          );
        })(),
        price: <td style={tdStyle}>{formatCurrency(listing.list_price)}</td>,
        status: (
          <td style={tdStyle}>
            <span style={{
              padding: "2px 6px", borderRadius: "4px", fontSize: "11px",
              fontWeight: "500", backgroundColor: getStatusColor(listing.listing_status).bg,
              color: getStatusColor(listing.listing_status).color,
            }}>
              {listing.listing_status}
            </span>
          </td>
        ),
        dw: <td style={tdStyle}>{formatBoolean(listing.dishwasher, listing.dishwasher_source)}</td>,
        wd: <td style={tdStyle}>{formatBoolean(listing.washer_dryer, listing.washer_dryer_source)}</td>,
        reno: <td style={tdStyle}>{formatWithSource(listing.renovation_level, "ai")}</td>,
        views: <td style={tdStyle}>{formatWithSource(listing.premium_views, "ai")}</td>,
        hoa: <td style={tdStyle}>{formatCurrency(listing.hoa)}</td>,
        tax: <td style={tdStyle}>{formatCurrency(listing.tax)}</td>,
        line: <td style={tdStyle}>{formatWithSource(listing.floor_line, listing.floor_line_source)}</td>,
        link: (
          <td style={tdStyle}>
            {listing.listing_url ? (
              <a href={listing.listing_url} target="_blank" rel="noopener noreferrer"
                style={{ color: "#1976d2", textDecoration: "none" }}
                onClick={(e) => e.stopPropagation()}>View</a>
            ) : "-"}
          </td>
        ),
        block: <td style={tdStyle}>{listing.block || "-"}</td>,
        lot: <td style={tdStyle}>{listing.lot || "-"}</td>,
        borough: <td style={tdStyle}>{listing.borough || "-"}</td>,
        acris: (
          <td style={tdStyle}>
            {listing.acris_url ? (
              <a href={listing.acris_url} target="_blank" rel="noopener noreferrer"
                style={{ color: "#1976d2", textDecoration: "none" }}
                onClick={(e) => e.stopPropagation()}>ACRIS</a>
            ) : "-"}
          </td>
        ),
        tax_abatement: (
          <td style={tdStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: "18px", height: "18px", borderRadius: "3px",
                backgroundColor: listing.tax_abatement ? "#e8f5e9" : "#f5f5f5",
                color: listing.tax_abatement ? "#2e7d32" : "#999",
                fontSize: "12px", fontWeight: "600",
              }}>
                {listing.tax_abatement ? "✓" : "-"}
              </span>
              {listing.tax_abatement_description && (
                <span
                  style={{ cursor: "help", fontSize: "14px", position: "relative" }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseEnter={() => setTaxNoteHover(listing.id)}
                  onMouseLeave={() => setTaxNoteHover(null)}
                >
                  📝
                  {taxNoteHover === listing.id && (
                    <div style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      marginBottom: "4px",
                      padding: "6px 10px",
                      backgroundColor: "#333",
                      color: "white",
                      borderRadius: "4px",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                      maxWidth: "250px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      zIndex: 1000,
                      pointerEvents: "none",
                    }}>
                      {listing.tax_abatement_description}
                    </div>
                  )}
                </span>
              )}
            </div>
          </td>
        ),
      };
    }

    // Edit mode cells
    return {
      unit: (
        <td style={editTdStyle}>
          <input type="text" value={editData.unit_number || ""} onChange={(e) => updateField("unit_number", e.target.value)}
            style={inputStyle} placeholder="Unit" />
        </td>
      ),
      floor: (
        <td style={editTdStyle}>
          <input type="text" value={editData.floor || ""} onChange={(e) => updateField("floor", e.target.value)}
            style={{ ...inputStyle, width: "50px" }} placeholder="Fl" />
        </td>
      ),
      beds: (
        <td style={editTdStyle}>
          <select value={editData.bedrooms ?? ""} onChange={(e) => updateField("bedrooms", e.target.value === "" ? null : parseFloat(e.target.value))}
            style={selectStyle}>
            {BEDROOM_OPTIONS.map((v) => (
              <option key={String(v)} value={v ?? ""}>{v === null ? "-" : v === 0 ? "Studio" : v}</option>
            ))}
          </select>
        </td>
      ),
      baths: (
        <td style={editTdStyle}>
          <select value={editData.bathrooms ?? ""} onChange={(e) => updateField("bathrooms", e.target.value === "" ? null : parseFloat(e.target.value))}
            style={selectStyle}>
            {BATHROOM_OPTIONS.map((v) => (
              <option key={String(v)} value={v ?? ""}>{v === null ? "-" : v}</option>
            ))}
          </select>
        </td>
      ),
      sqft: (
        <td style={editTdStyle}>
          <input type="number" value={editData.sqft ?? ""} onChange={(e) => updateField("sqft", e.target.value === "" ? null : e.target.value)}
            style={{ ...inputStyle, width: "70px" }} placeholder="SqFt" />
          {/* Can't find sqft? one click to drop in the auto-estimated value */}
          {listing.estimated_sqft && (
            <button
              onClick={(e) => { e.stopPropagation(); updateField("sqft", listing.estimated_sqft); }}
              style={{ display: "block", marginTop: "3px", fontSize: "9px", fontWeight: 600, padding: "1px 5px",
                borderRadius: 3, background: "#eef2ff", color: "#3949ab", border: "1px solid #c5cae9", cursor: "pointer", whiteSpace: "nowrap" }}
              title="Use the auto-estimated sqft"
            >use est {listing.estimated_sqft.toLocaleString()}</button>
          )}
        </td>
      ),
      price: (
        <td style={editTdStyle}>
          <input type="number" value={editData.list_price ?? ""} onChange={(e) => updateField("list_price", e.target.value === "" ? null : e.target.value)}
            style={{ ...inputStyle, width: "100px" }} placeholder="Price" />
        </td>
      ),
      status: (
        <td style={editTdStyle}>
          <select value={editData.listing_status || ""} onChange={(e) => updateField("listing_status", e.target.value)}
            style={selectStyle}>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </td>
      ),
      dw: (
        <td style={editTdStyle}>
          <select value={editData.dishwasher === null ? "" : editData.dishwasher === true ? "true" : "false"}
            onChange={(e) => updateField("dishwasher", e.target.value === "" ? null : e.target.value === "true")}
            style={selectStyle}>
            {YES_NO_UNKNOWN.map((o) => (
              <option key={String(o.value)} value={o.value === null ? "" : String(o.value)}>{o.label}</option>
            ))}
          </select>
        </td>
      ),
      wd: (
        <td style={editTdStyle}>
          <select value={editData.washer_dryer ?? ""} onChange={(e) => updateField("washer_dryer", e.target.value || null)}
            style={selectStyle}>
            {WASHER_DRYER_OPTIONS.map((o) => (
              <option key={String(o.value)} value={o.value ?? ""}>{o.label}</option>
            ))}
          </select>
        </td>
      ),
      reno: (
        <td style={editTdStyle}>
          <select value={editData.renovation_level ?? ""} onChange={(e) => updateField("renovation_level", e.target.value || null)}
            style={selectStyle}>
            {RENOVATION_OPTIONS.map((o) => (
              <option key={String(o.value)} value={o.value ?? ""}>{o.label}</option>
            ))}
          </select>
        </td>
      ),
      views: (
        <td style={editTdStyle}>
          <select value={editData.premium_views ?? ""} onChange={(e) => updateField("premium_views", e.target.value || null)}
            style={selectStyle}>
            {VIEWS_OPTIONS.map((o) => (
              <option key={String(o.value)} value={o.value ?? ""}>{o.label}</option>
            ))}
          </select>
        </td>
      ),
      hoa: (
        <td style={editTdStyle}>
          <input type="number" value={editData.hoa ?? ""} onChange={(e) => updateField("hoa", e.target.value === "" ? null : e.target.value)}
            style={{ ...inputStyle, width: "70px" }} placeholder="HOA" />
        </td>
      ),
      tax: (
        <td style={editTdStyle}>
          <input type="number" value={editData.tax ?? ""} onChange={(e) => updateField("tax", e.target.value === "" ? null : e.target.value)}
            style={{ ...inputStyle, width: "70px" }} placeholder="Tax" />
        </td>
      ),
      line: (
        <td style={editTdStyle}>
          <input type="text" value={editData.floor_line || ""} onChange={(e) => updateField("floor_line", e.target.value)}
            style={{ ...inputStyle, width: "50px" }} placeholder="Line" />
        </td>
      ),
      link: (
        <td style={editTdStyle}>
          <input type="text" value={editData.listing_url || ""} onChange={(e) => updateField("listing_url", e.target.value)}
            style={{ ...inputStyle, width: "140px" }} placeholder="URL" />
        </td>
      ),
      block: (
        <td style={editTdStyle}>
          <input type="text" value={editData.block || ""} onChange={(e) => updateField("block", e.target.value)}
            style={{ ...inputStyle, width: "60px" }} placeholder="Block" />
        </td>
      ),
      lot: (
        <td style={editTdStyle}>
          <input type="text" value={editData.lot || ""} onChange={(e) => updateField("lot", e.target.value)}
            style={{ ...inputStyle, width: "50px" }} placeholder="Lot" />
        </td>
      ),
      borough: (
        <td style={editTdStyle}>
          <input type="text" value={editData.borough || ""} onChange={(e) => updateField("borough", e.target.value)}
            style={{ ...inputStyle, width: "30px" }} placeholder="Boro" />
        </td>
      ),
      acris: (
        <td style={editTdStyle}>
          <input type="text" value={editData.acris_url || ""} onChange={(e) => updateField("acris_url", e.target.value)}
            style={{ ...inputStyle, width: "140px" }} placeholder="ACRIS URL" />
        </td>
      ),
      tax_abatement: (
        <td style={editTdStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "4px", position: "relative" }}>
            <input
              type="checkbox"
              checked={editData.tax_abatement || false}
              onChange={(e) => updateField("tax_abatement", e.target.checked)}
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
            <span
              onClick={(e) => {
                e.stopPropagation();
                setTaxNotePopover(taxNotePopover === listing.id ? null : listing.id);
              }}
              style={{
                cursor: "pointer",
                fontSize: "14px",
                opacity: editData.tax_abatement_description ? 1 : 0.4,
              }}
              title={editData.tax_abatement_description || "Add notes"}
            >📝</span>
            {taxNotePopover === listing.id && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  zIndex: 1000,
                  backgroundColor: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  padding: "8px",
                  width: "250px",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <textarea
                  value={editData.tax_abatement_description || ""}
                  onChange={(e) => updateField("tax_abatement_description", e.target.value)}
                  placeholder="Tax abatement details..."
                  style={{
                    width: "100%",
                    minHeight: "80px",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "12px",
                    resize: "vertical",
                  }}
                />
                <button
                  onClick={() => setTaxNotePopover(null)}
                  style={{
                    marginTop: "6px",
                    padding: "4px 12px",
                    fontSize: "12px",
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >Done</button>
              </div>
            )}
          </div>
        </td>
      ),
    };
  };

  return (
    <>
      <Header showLogout={true} />
      <div style={{ padding: "40px", maxWidth: "1800px", margin: "0 auto" }}>
        <h1 style={{ marginBottom: "10px" }}>Listings Data Quality</h1>
        <p style={{ color: "#666", marginBottom: "20px" }}>
          Manage and edit listing data for accuracy. Click the pencil icon to edit.
        </p>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "0", marginBottom: "24px", borderBottom: "2px solid #e0e0e0" }}>
          {[
            { key: "unverified", label: "To Review" },
            { key: "verified", label: "Verified" },
            { key: "duplicates", label: "Duplicates" },
            { key: "excluded", label: "Excluded (Co-ops)" },
            { key: "townhouses", label: "Townhouses / Houses" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setPage(1); setEditingId(null); if (tab.key === "excluded" || tab.key === "townhouses") setOwnershipType(""); if (tab.key === "excluded") setSaleOrRental(""); }}
              style={{
                padding: "10px 24px",
                fontSize: "14px",
                fontWeight: activeTab === tab.key ? "600" : "400",
                color: activeTab === tab.key ? "#000" : "#666",
                backgroundColor: "transparent",
                border: "none",
                borderBottom: activeTab === tab.key ? "2px solid #000" : "2px solid transparent",
                marginBottom: "-2px",
                cursor: "pointer",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Listing ID Filter Banner */}
        {filterListingId && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "12px 16px",
            backgroundColor: "#e3f2fd",
            borderRadius: "8px",
            marginBottom: "16px"
          }}>
            <span style={{ fontSize: "14px", color: "#1565c0" }}>
              Showing listing #{filterListingId}
            </span>
            <button
              onClick={() => { setFilterListingId(null); setPage(1); }}
              style={{
                padding: "6px 12px",
                backgroundColor: "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              Show All Listings
            </button>
          </div>
        )}

        {/* Filters Row */}
        <div style={{ display: "flex", gap: "16px", marginBottom: "24px", alignItems: "flex-end", flexWrap: "wrap" }}>
          <form onSubmit={handleSearch} style={{ display: "flex", gap: "8px" }}>
            <input type="text" placeholder="Search by address... (use # for unit)" value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ padding: "10px 16px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px", width: "350px" }} />
            <button type="submit"
              style={{ padding: "10px 20px", backgroundColor: "#000", color: "#fff", border: "none", borderRadius: "6px", fontSize: "14px", fontWeight: "500", cursor: "pointer" }}>
              Search
            </button>
            {search && (
              <button type="button" onClick={() => { setSearchInput(""); setSearch(""); setPage(1); }}
                style={{ padding: "10px 20px", backgroundColor: "#f5f5f5", color: "#333", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px", cursor: "pointer" }}>
                Clear
              </button>
            )}
          </form>
          {activeTab !== "excluded" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>Type</label>
              <select value={saleOrRental} onChange={(e) => { setSaleOrRental(e.target.value); setPage(1); }}
                style={{ padding: "10px 16px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px", minWidth: "120px", backgroundColor: "#fff" }}>
                <option value="">All</option>
                <option value="S">Sales</option>
                <option value="R">Rentals</option>
              </select>
            </div>
          )}
          {(activeTab === "unverified" || activeTab === "verified") && (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>Ownership</label>
              <select value={ownershipType} onChange={(e) => { setOwnershipType(e.target.value); setPage(1); }}
                style={{ padding: "10px 16px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px", minWidth: "120px", backgroundColor: "#fff" }}>
                <option value="">All</option>
                <option value="condo">Condo</option>
                <option value="coop">Co-op</option>
                <option value="rental">Rental</option>
                <option value="townhouse">Townhouse / House</option>
                <option value="multi">Multi Family</option>
              </select>
            </div>
          )}
          {(activeTab === "unverified" || activeTab === "verified") && (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>Status</label>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                style={{ padding: "10px 16px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px", minWidth: "180px", backgroundColor: "#fff" }}>
                <option value="active">Active Listings</option>
                <option value="closed_12m">Closed (Last 12 Months)</option>
                <option value="closed_1_3y">Closed (1–3 Years Ago)</option>
                <option value="closed_3y_plus">Closed (3+ Years Ago)</option>
              </select>
            </div>
          )}
          <NeighborhoodSelect
            neighborhoods={neighborhoods}
            selected={selectedNeighborhood}
            onChange={(val) => { setSelectedNeighborhood(val); setPage(1); }}
            multi={false}
            label="Neighborhood"
          />
          <button
            onClick={() => { setShowAddModal(true); setAddError(""); setAddSuccess(""); }}
            style={{
              padding: "10px 20px", backgroundColor: "#1976d2", color: "#fff",
              border: "none", borderRadius: "6px", fontSize: "14px", fontWeight: "500",
              cursor: "pointer", alignSelf: "flex-end",
            }}>
            + Add Listing
          </button>
        </div>

        {/* Add from RealPlus Modal */}
        {showAddModal && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }} onClick={() => setShowAddModal(false)}>
            <div style={{
              backgroundColor: "#fff", borderRadius: "12px", padding: "32px",
              width: "400px", maxWidth: "90vw", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                <h2 style={{ margin: 0, fontSize: "18px" }}>Add Listing from RealPlus</h2>
                <button onClick={() => setShowAddModal(false)}
                  style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#999" }}>
                  ×
                </button>
              </div>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "13px", color: "#666", marginBottom: "6px", fontWeight: "500" }}>
                  RealPlus ID
                </label>
                <input
                  type="number"
                  value={addRpId}
                  onChange={(e) => setAddRpId(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addFromRealPlus(); }}
                  placeholder="e.g. 9252138"
                  autoFocus
                  style={{
                    width: "100%", padding: "10px 14px", border: "1px solid #ddd",
                    borderRadius: "6px", fontSize: "14px", boxSizing: "border-box",
                  }}
                />
              </div>
              {addError && (
                <div style={{ color: "#d32f2f", fontSize: "13px", marginBottom: "12px" }}>
                  {addError}
                </div>
              )}
              {addSuccess && (
                <div style={{ color: "#2e7d32", fontSize: "13px", marginBottom: "12px" }}>
                  {addSuccess}
                </div>
              )}
              <button
                onClick={addFromRealPlus}
                disabled={!addRpId || addLoading}
                style={{
                  width: "100%", padding: "10px", backgroundColor: !addRpId || addLoading ? "#ccc" : "#000",
                  color: "#fff", border: "none", borderRadius: "6px", fontSize: "14px",
                  fontWeight: "500", cursor: !addRpId || addLoading ? "not-allowed" : "pointer",
                }}>
                {addLoading ? "Fetching from RealPlus..." : "Add Listing"}
              </button>
            </div>
          </div>
        )}

        {/* Co-op Building Approval Modal */}
        {showCoopModal && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }} onClick={() => setShowCoopModal(null)}>
            <div style={{
              backgroundColor: "#fff", borderRadius: "12px", padding: "32px",
              width: "500px", maxWidth: "90vw", maxHeight: "90vh", overflowY: "auto",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <h2 style={{ margin: 0, fontSize: "18px" }}>Approve Building</h2>
                <button onClick={() => setShowCoopModal(null)}
                  style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#999" }}>
                  ×
                </button>
              </div>
              <p style={{ fontSize: "13px", color: "#666", marginBottom: "20px" }}>
                {showCoopModal.address} - All units in this building will be approved.
              </p>

              <div style={{ backgroundColor: "#fff3e0", padding: "16px", borderRadius: "8px", border: "2px solid #ff9800" }}>
                <div style={{ fontSize: "15px", fontWeight: "700", color: "#e65100", marginBottom: "16px" }}>
                  Co-op Policies
                </div>

                {/* Sublet Policy - REQUIRED */}
                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>
                    Sublet Policy <span style={{ color: "#c62828" }}>*</span>
                  </label>
                  <select
                    value={coopForm.sublet_policy}
                    onChange={(e) => setCoopForm((f) => ({ ...f, sublet_policy: e.target.value }))}
                    style={{ width: "100%", padding: "8px 12px", border: coopForm.sublet_policy ? "1px solid #ddd" : "2px solid #c62828", borderRadius: "6px", fontSize: "14px" }}
                  >
                    <option value="">Select...</option>
                    <option value="not_allowed">Not Allowed</option>
                    <option value="allowed">Allowed</option>
                    <option value="vesting_period">Vesting Period</option>
                    <option value="sometimes">Sometimes</option>
                  </select>
                  {coopForm.sublet_policy === "vesting_period" && (
                    <input type="text" placeholder="Enter vesting period details (e.g., 2 years)"
                      value={coopForm.sublet_vesting_details}
                      onChange={(e) => setCoopForm((f) => ({ ...f, sublet_vesting_details: e.target.value }))}
                      style={{ width: "100%", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px", marginTop: "8px", boxSizing: "border-box" }}
                    />
                  )}
                  {coopForm.sublet_policy === "sometimes" && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                      <input type="text" placeholder="X"
                        value={coopForm.sublet_sometimes_numerator}
                        onChange={(e) => setCoopForm((f) => ({ ...f, sublet_sometimes_numerator: e.target.value }))}
                        style={{ flex: 1, padding: "8px 12px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px" }}
                      />
                      <span style={{ fontSize: "14px", color: "#666" }}>out of</span>
                      <input type="text" placeholder="Y"
                        value={coopForm.sublet_sometimes_denominator}
                        onChange={(e) => setCoopForm((f) => ({ ...f, sublet_sometimes_denominator: e.target.value }))}
                        style={{ flex: 1, padding: "8px 12px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px" }}
                      />
                    </div>
                  )}
                </div>

                {/* International Buyers */}
                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>Allow International Buyers</label>
                  <select value={coopForm.international_buyers}
                    onChange={(e) => setCoopForm((f) => ({ ...f, international_buyers: e.target.value }))}
                    style={{ width: "100%", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px" }}>
                    <option value="">N/A</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>

                {/* LLC Buyer */}
                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>Allow LLC Buyer</label>
                  <select value={coopForm.llc_buyer}
                    onChange={(e) => setCoopForm((f) => ({ ...f, llc_buyer: e.target.value }))}
                    style={{ width: "100%", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px" }}>
                    <option value="">N/A</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>

                {/* Board Interview for Rentals */}
                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>Requires Board Interview for Rentals</label>
                  <select value={coopForm.board_interview_rentals}
                    onChange={(e) => setCoopForm((f) => ({ ...f, board_interview_rentals: e.target.value }))}
                    style={{ width: "100%", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px" }}>
                    <option value="">N/A</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>

                {/* Board Interview for Sales */}
                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>Requires Board Interview for Sales</label>
                  <select value={coopForm.board_interview_sales}
                    onChange={(e) => setCoopForm((f) => ({ ...f, board_interview_sales: e.target.value }))}
                    style={{ width: "100%", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px" }}>
                    <option value="">N/A</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>

                {/* Special Notes */}
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>Special Notes / Policies</label>
                  <textarea
                    value={coopForm.special_notes}
                    onChange={(e) => setCoopForm((f) => ({ ...f, special_notes: e.target.value }))}
                    placeholder="Special policies, board approval notes, etc."
                    rows={3}
                    style={{ width: "100%", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px", resize: "vertical", boxSizing: "border-box" }}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "12px", marginTop: "20px" }}>
                <button
                  onClick={saveCoopPoliciesOnly}
                  disabled={coopSaving}
                  style={{
                    flex: 1, padding: "12px",
                    backgroundColor: coopSaving ? "#ccc" : "#fff",
                    color: coopSaving ? "#999" : "#e65100", border: "2px solid #e65100", borderRadius: "6px", fontSize: "14px",
                    fontWeight: "600", cursor: coopSaving ? "not-allowed" : "pointer",
                  }}>
                  {coopSaving ? "Saving..." : "Save Only"}
                </button>
                <button
                  onClick={submitCoopApproval}
                  disabled={!coopForm.sublet_policy || coopSaving}
                  style={{
                    flex: 1, padding: "12px",
                    backgroundColor: !coopForm.sublet_policy || coopSaving ? "#ccc" : "#e65100",
                    color: "#fff", border: "none", borderRadius: "6px", fontSize: "14px",
                    fontWeight: "600", cursor: !coopForm.sublet_policy || coopSaving ? "not-allowed" : "pointer",
                  }}>
                  {coopSaving ? "Approving..." : "Approve Building"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reno suggestion modal */}
        {renoSuggestion && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.4)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <div style={{
              backgroundColor: "#fff", borderRadius: "12px", padding: "24px",
              maxWidth: renoSuggestion.expanded ? "700px" : "480px", width: "90%",
              maxHeight: "80vh", overflowY: "auto",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}>
              <h3 style={{ margin: "0 0 12px", fontSize: "16px" }}>Renovation Level Update</h3>
              <div style={{ fontSize: "14px", color: "#333", marginBottom: "8px" }}>
                <b>{renoSuggestion.address}</b>
              </div>
              <div style={{
                padding: "10px 14px", backgroundColor: "#fff3e0", border: "1px solid #ffb74d",
                borderRadius: "8px", fontSize: "13px", marginBottom: "12px",
              }}>
                Current: <b>{renoSuggestion.current || "none"}</b> → Suggested: <b>{renoSuggestion.suggested}</b>
                <div style={{ color: "#666", marginTop: "4px" }}>{renoSuggestion.reasoning}</div>
              </div>

              {renoSuggestion.expanded && (
                <>
                  {renoSuggestion.description && (
                    <div style={{ marginBottom: "12px" }}>
                      <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "4px" }}>Description</div>
                      <div style={{
                        fontSize: "12px", color: "#555", maxHeight: "150px", overflowY: "auto",
                        padding: "8px", backgroundColor: "#f9f9f9", borderRadius: "6px",
                        whiteSpace: "pre-wrap",
                      }}>{renoSuggestion.description}</div>
                    </div>
                  )}
                  {renoSuggestion.images.length > 0 && (
                    <div style={{ marginBottom: "12px" }}>
                      <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "4px" }}>
                        Images ({renoSuggestion.images.length})
                      </div>
                      <div style={{
                        display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "6px",
                        maxHeight: "300px", overflowY: "auto",
                      }}>
                        {renoSuggestion.images.map((img, i) => (
                          <img key={i} src={img} alt={`Unit ${i + 1}`} style={{
                            width: "100%", borderRadius: "6px", cursor: "pointer",
                          }} onClick={() => window.open(img, "_blank")} />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px" }}>
                <button onClick={() => setRenoSuggestion(null)} style={{
                  padding: "8px 16px", borderRadius: "6px", border: "1px solid #ccc",
                  backgroundColor: "#f5f5f5", color: "#666", cursor: "pointer", fontSize: "13px",
                }}>Cancel</button>
                {!renoSuggestion.expanded ? (
                  <button onClick={() => setRenoSuggestion(prev => ({ ...prev, expanded: true }))} style={{
                    padding: "8px 16px", borderRadius: "6px", border: "1px solid #1976d2",
                    backgroundColor: "#e3f2fd", color: "#1976d2", cursor: "pointer", fontSize: "13px", fontWeight: 600,
                  }}>See More</button>
                ) : (
                  <button onClick={acceptRenoSuggestion} style={{
                    padding: "8px 16px", borderRadius: "6px", border: "1px solid #2e7d32",
                    backgroundColor: "#e8f5e9", color: "#2e7d32", cursor: "pointer", fontSize: "13px", fontWeight: 600,
                  }}>Confirm</button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Results count */}
        <div style={{ marginBottom: "16px", color: "#666", fontSize: "14px" }}>
          {formatNumber(total)} listing{total !== 1 ? "s" : ""} found
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>Loading listings...</div>
        ) : (
          <>
            <div style={{ display: "flex", gap: "16px", marginBottom: "8px", fontSize: "12px", color: "#666", flexWrap: "wrap" }}>
              <span>DW/WD:</span>
              <span style={{ color: "inherit" }}>Black = Actual</span>
              <span style={{ color: "#7b1fa2" }}>Purple* = AI (high conf)</span>
              <span style={{ color: "#64b5f6" }}>Blue* = AI (inferred)</span>
              <span style={{ margin: "0 4px", color: "#ccc" }}>|</span>
              <span>Address:</span>
              <span style={{ color: "#2e7d32" }}>✓ = Building verified</span>
              <span style={{ margin: "0 4px", color: "#ccc" }}>|</span>
              <span>SE:</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", borderRadius: "50%", fontSize: "8px", fontWeight: "700", border: "1px solid #ccc", backgroundColor: "#f5f5f5", color: "#999" }}>SE</span>
                No URL
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", borderRadius: "50%", fontSize: "8px", fontWeight: "700", border: "1px solid #1565c0", backgroundColor: "#e3f2fd", color: "#1565c0" }}>SE</span>
                Has URL
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", borderRadius: "50%", fontSize: "8px", fontWeight: "700", border: "1px solid #2e7d32", backgroundColor: "#e8f5e9", color: "#2e7d32" }}>SE</span>
                Enriched
              </span>
            </div>
            <div style={{ border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "auto", marginBottom: "24px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", minWidth: "2000px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    <th style={{ ...thStyle, width: "32px" }}></th>
                    <th style={{ ...thStyle, fontSize: "11px", color: "#999" }}>ID</th>
                    <th style={thStyle}>Type</th>
                    <SortableHeader label="Date Listed" field="created_date" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    <th style={{ ...thStyle, fontSize: "11px" }}>DOM</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Own</th>
                    {activeTab === "unverified" && <th style={thStyle}>Needs</th>}
                    {activeTab === "excluded" && <th style={thStyle}>Reason</th>}
                    <SortableHeader label="Address" field="address" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    <th style={thStyle}>Link</th>
                    <th style={{ ...thStyle, width: "36px" }}>SE</th>
                    <SortableHeader label="Neighborhood" field="neighborhood" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    <th style={thStyle}>Unit</th>
                    <th style={thStyle}>Floor</th>
                    <SortableHeader label="Beds" field="bedrooms" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    <SortableHeader label="Baths" field="bathrooms" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    <SortableHeader label="SqFt" field="sqft" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    <th style={thStyle}>Line</th>
                    <SortableHeader label="Price" field="list_price" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                    <th style={thStyle}>DW</th>
                    <th style={thStyle}>W/D</th>
                    <th style={thStyle}>Outdoor</th>
                    <th style={thStyle}>Reno</th>
                    <th style={thStyle}>HOA</th>
                    <th style={thStyle}>Tax</th>
                    {/* Tax detail columns (Tax Abmt/Boro/Block/Lot/ACRIS) moved into the
                        inline tax sub-form revealed when editing a sale row. */}
                    {activeTab === "excluded" && <th style={thStyle}></th>}
                  </tr>
                </thead>
                <tbody>
                  {listings.map((l) => {
                    const isEditing = editingId === l.id;
                    const cells = renderEditCell(l);
                    const seStatus = getSeStatus(l);
                    const isSeLoading = enrichingId === l.id || seSearchingId === l.id;
                    const isLastEnriched = lastEnrichedId === l.id;
                    const rowBg = isEditing ? "#fffde7" : isLastEnriched ? "#e8f5e9" : "transparent";
                    return (
                      <Fragment key={l.id}>
                      <tr
                        ref={isEditing ? editRowRef : null}
                        style={{
                          borderBottom: "1px solid #e0e0e0",
                          backgroundColor: rowBg,
                          transition: "background-color 0.3s",
                        }}
                        onMouseEnter={(e) => { if (!isEditing && !isLastEnriched) e.currentTarget.style.backgroundColor = "#fafafa"; }}
                        onMouseLeave={(e) => { if (!isEditing) e.currentTarget.style.backgroundColor = isLastEnriched ? "#e8f5e9" : "transparent"; }}
                      >
                        {/* Edit icon */}
                        <td style={{ ...tdStyle, padding: "6px 4px", textAlign: "center" }}>
                          {isEditing ? (
                            <div style={{ display: "flex", gap: "2px" }}>
                              <button onClick={saveEditing} disabled={saving} title="Save"
                                style={{ ...actionBtnStyle, backgroundColor: "#2e7d32", color: "#fff", border: "none", padding: "2px 6px", fontSize: "10px" }}>
                                {saving ? "..." : "Save"}
                              </button>
                              <button onClick={cancelEditing} title="Cancel"
                                style={{ ...actionBtnStyle, backgroundColor: "#f5f5f5", color: "#333", padding: "2px 6px", fontSize: "10px" }}>
                                X
                              </button>
                            </div>
                          ) : (
                            <span style={{ display: "flex", gap: "7px", alignItems: "center" }}>
                              {/* Inline quick-edit — primary action: flip the row to edit mode */}
                              <button
                                onClick={(e) => { e.stopPropagation(); startEditing(l); }}
                                style={{ cursor: "pointer", fontSize: "11px", fontWeight: 700, padding: "3px 9px",
                                  borderRadius: 4, background: "#1565c0", color: "#fff", border: "none" }}
                                title="Quick edit (inline)"
                              >Edit</button>
                              {/* Full edit modal */}
                              <span
                                onClick={(e) => { e.stopPropagation(); setEditModalListing(l); }}
                                style={{ cursor: "pointer", fontSize: "17px", color: "#1565c0" }}
                                title="Full edit (modal)"
                              >&#9998;</span>
                              {/* Image review */}
                              <span
                                onClick={(e) => { e.stopPropagation(); setImageReviewListing(l); }}
                                style={{ cursor: "pointer", fontSize: "17px", color: "#6a1b9a" }}
                                title="Image review"
                              >&#128247;</span>
                              {l.duplicate_of && (
                                <span
                                  onClick={(e) => { e.stopPropagation(); setMergeListingId(l.id); }}
                                  style={{ cursor: "pointer", fontSize: "11px", fontWeight: 600, padding: "1px 6px", borderRadius: 3, background: "#ff9800", color: "#fff" }}
                                  title="Merge duplicate"
                                >MERGE</span>
                              )}
                            </span>
                          )}
                        </td>
                        {/* ID */}
                        <td style={{ ...tdStyle, fontSize: "11px", color: "#999" }}>{l.id}</td>
                        {/* Type */}
                        <td style={tdStyle}>
                          <span style={{
                            padding: "2px 6px", borderRadius: "4px", fontSize: "11px", fontWeight: "500",
                            backgroundColor: l.sale_or_rental === "S" ? "#e8f5e9" : "#e3f2fd",
                            color: l.sale_or_rental === "S" ? "#2e7d32" : "#1565c0",
                          }}>
                            {l.sale_or_rental === "S" ? "Sale" : "Rental"}
                          </span>
                        </td>
                        {/* Date Listed */}
                        <td style={tdStyle}>{formatDate(l.created_date)}</td>
                        {/* Days on Market */}
                        <td style={{ ...tdStyle, fontSize: "12px", color: (() => { const endDate = l.closing_date ? new Date(l.closing_date) : new Date(); const d = l.created_date ? Math.floor((endDate - new Date(l.created_date)) / 86400000) : null; return d > 90 ? "#c62828" : d > 30 ? "#e65100" : "#666"; })() }}>
                          {l.created_date ? Math.max(0, Math.floor(((l.closing_date ? new Date(l.closing_date) : new Date()) - new Date(l.created_date)) / 86400000)) : "-"}
                        </td>
                        {/* Status */}
                        {cells.status}
                        {/* Ownership */}
                        <td style={tdStyle}>
                          <span style={{
                            fontSize: "11px", fontWeight: "500",
                            color: l.condo_coop === "Co-op" ? "#e65100" : "#2e7d32",
                          }}>
                            {l.condo_coop || "-"}
                          </span>
                        </td>
                        {/* Needs — gate-flagged fields the human must verify */}
                        {activeTab === "unverified" && (
                          <td style={{ ...tdStyle, maxWidth: "160px" }}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "3px" }}>
                              {(l.review_reasons || []).map((r) => (
                                <span key={r} style={{
                                  fontSize: "10px", fontWeight: 600, padding: "1px 6px", borderRadius: "10px",
                                  background: "#fff3e0", color: "#e65100", border: "1px solid #ffcc80",
                                  whiteSpace: "nowrap",
                                }}>
                                  {REASON_LABELS[r] || r}
                                </span>
                              ))}
                            </div>
                            {/* Whole-building units: defer to Townhouse tab or soft-delete */}
                            {(l.review_reasons || []).includes("whole_building_unit") && (
                              <div style={{ display: "flex", gap: "4px", marginTop: "4px" }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setRowStatus(l.id, "TOWNHOUSE"); }}
                                  style={{ fontSize: "10px", fontWeight: 600, padding: "2px 6px", borderRadius: 3,
                                    background: "#fb8c00", color: "#fff", border: "none", cursor: "pointer" }}
                                  title="Move to the Townhouses tab"
                                >→ Townhouse</button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); if (window.confirm("Soft-delete this listing?")) setRowStatus(l.id, "DELETED"); }}
                                  style={{ fontSize: "10px", fontWeight: 600, padding: "2px 6px", borderRadius: 3,
                                    background: "#e53935", color: "#fff", border: "none", cursor: "pointer" }}
                                  title="Soft-delete (internal_status=DELETED)"
                                >Delete</button>
                              </div>
                            )}
                          </td>
                        )}
                        {/* Exclusion Reason */}
                        {activeTab === "excluded" && (
                          <td style={{ ...tdStyle, fontSize: "11px", color: "#c62828" }}>
                            {l.exclusion_reason || "-"}
                          </td>
                        )}
                        {/* Address */}
                        <td style={{ ...tdStyle, maxWidth: "200px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                            {l.building_verified && (
                              <span style={{ color: "#2e7d32", fontSize: "12px", flexShrink: 0 }} title="Building verified">✓</span>
                            )}
                            {l.property_id ? (
                              <span
                                style={{ color: "#1976d2", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}
                                onClick={(e) => { e.stopPropagation(); loadBuildingDetail(l.property_id); }}
                                title="View building details"
                              >
                                {l.address?.trim() || "-"}
                              </span>
                            ) : (
                              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {l.address?.trim() || "-"}
                              </div>
                            )}
                          </div>
                        </td>
                        {/* Link */}
                        {cells.link}
                        {/* SE Icon */}
                        <td style={{ ...tdStyle, padding: "6px 4px", position: "relative" }}>
                          {isSeLoading ? (
                            <span style={{ fontSize: "11px", color: "#999" }}>...</span>
                          ) : (
                            <span
                              onClick={(e) => { e.stopPropagation(); handleSeClick(l); }}
                              style={{
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                width: "24px", height: "24px", borderRadius: "50%", fontSize: "10px",
                                fontWeight: "700", cursor: "pointer", border: "1px solid",
                                backgroundColor: seStatus === "enriched" ? "#e8f5e9" : seStatus === "has_url" ? "#e3f2fd" : "#ffebee",
                                color: seStatus === "enriched" ? "#2e7d32" : seStatus === "has_url" ? "#1565c0" : "#c62828",
                                borderColor: seStatus === "enriched" ? "#2e7d32" : seStatus === "has_url" ? "#1565c0" : "#c62828",
                              }}
                              title={seStatus === "enriched" ? "SE enriched - click to re-fetch" : seStatus === "has_url" ? "Has SE URL - click to fetch" : "No SE URL - click to search"}
                            >
                              SE
                            </span>
                          )}
                          {seManualUrlId === l.id && (
                            <div style={{
                              position: "absolute", top: "100%", left: 0, zIndex: 1000,
                              backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: "6px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.15)", padding: "8px", width: "280px",
                            }} onClick={(e) => e.stopPropagation()}>
                              <div style={{ fontSize: "11px", color: "#c62828", marginBottom: "6px" }}>
                                SE URL not found. Paste manually:
                              </div>
                              <div style={{ display: "flex", gap: "4px" }}>
                                <input
                                  type="text" value={seManualUrl} autoFocus
                                  onChange={(e) => setSeManualUrl(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === "Enter") handleSeManualSubmit(l); }}
                                  placeholder="streeteasy.com/..."
                                  style={{ flex: 1, padding: "4px 6px", border: "1px solid #bbb", borderRadius: "4px", fontSize: "11px" }}
                                />
                                <button onClick={() => handleSeManualSubmit(l)}
                                  disabled={!seManualUrl || enrichingId === l.id}
                                  style={{ ...actionBtnStyle, backgroundColor: "#1976d2", color: "#fff", border: "none", fontSize: "10px" }}>
                                  Fetch
                                </button>
                                <button onClick={() => { setSeManualUrlId(null); setSeManualUrl(""); }}
                                  style={{ ...actionBtnStyle, backgroundColor: "#f5f5f5", color: "#333", fontSize: "10px" }}>
                                  X
                                </button>
                              </div>
                            </div>
                          )}
                        </td>
                        {/* Neighborhood */}
                        <td style={{ ...tdStyle, maxWidth: "150px", fontSize: "12px", color: "#555" }}>
                          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {l.neighborhood || "-"}
                          </div>
                        </td>
                        {/* Unit details */}
                        {cells.unit}
                        {cells.floor}
                        {cells.beds}
                        {cells.baths}
                        {cells.sqft}
                        {cells.line}
                        {cells.price}
                        {/* Amenities */}
                        {cells.dw}
                        {cells.wd}
                        {/* Outdoor */}
                        <td style={tdStyle}>
                          {l.private_outdoor_space === true ? "Yes" : l.private_outdoor_space === false ? "No" : "-"}
                        </td>
                        {cells.reno}
                        {cells.hoa}
                        {cells.tax}
                        {/* Tax-detail cells (Tax Abmt/Boro/Block/Lot/ACRIS) now live in the
                            inline tax sub-form (edit mode) — removed from the row to de-dup. */}
                        {/* Co-op actions (excluded tab only) */}
                        {activeTab === "excluded" && (
                          <td style={tdStyle}>
                            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); approveUnit(l.id); }}
                                style={{ ...actionBtnStyle, backgroundColor: "#1976d2", color: "#fff", border: "none" }}
                              >
                                Unit
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); openCoopApproval(l); }}
                                style={{ ...actionBtnStyle, backgroundColor: "#e65100", color: "#fff", border: "none" }}
                              >
                                Building
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                      {/* Inline tax sub-form — revealed under a SALE row while editing, so
                          tax/abatement/BBL/ACRIS are all fillable without the modal */}
                      {isEditing && l.sale_or_rental === "S" && (
                        <tr style={{ backgroundColor: "#fffde7", borderBottom: "2px solid #e0e0e0" }}>
                          <td colSpan={40} style={{ padding: "10px 18px 14px" }}>
                            <div style={{ display: "flex", gap: "18px", alignItems: "flex-end", flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 700, color: "#e65100", fontSize: "12px", marginRight: "4px" }}>Tax details</span>
                              <label style={taxSubLabel}>Tax ($/yr)
                                <input type="number" value={editData.tax ?? ""} onChange={(e) => updateField("tax", e.target.value)} style={taxSubInput} />
                              </label>
                              <label style={taxSubLabel}>Abatement
                                <select value={editData.tax_abatement ? "yes" : "no"} onChange={(e) => updateField("tax_abatement", e.target.value === "yes")} style={taxSubInput}>
                                  <option value="no">No</option>
                                  <option value="yes">Yes</option>
                                </select>
                              </label>
                              {editData.tax_abatement && (
                                <>
                                  <label style={taxSubLabel}>Type
                                    <select value={editData.tax_abatement_type ?? ""} onChange={(e) => updateField("tax_abatement_type", e.target.value)} style={taxSubInput}>
                                      <option value="">--</option>
                                      <option value="421a">421a</option>
                                      <option value="j-51">J-51</option>
                                      <option value="condo_abatement">Condo Abatement</option>
                                      <option value="other">Other</option>
                                    </select>
                                  </label>
                                  {editData.tax_abatement_type === "other" && (
                                    <label style={taxSubLabel}>Other type
                                      <input value={editData.tax_abatement_type_other ?? ""} onChange={(e) => updateField("tax_abatement_type_other", e.target.value)} style={taxSubInput} />
                                    </label>
                                  )}
                                  <label style={taxSubLabel}>Expires (Year)
                                    <input type="number" min="2020" max="2060" placeholder="2034" value={editData.tax_abatement_verified_date ?? ""} onChange={(e) => updateField("tax_abatement_verified_date", e.target.value)} style={{ ...taxSubInput, width: "80px" }} />
                                  </label>
                                  <label style={taxSubLabel}>Tax w/ abatement
                                    <input type="number" value={editData.tax_abatement_with ?? ""} onChange={(e) => updateField("tax_abatement_with", e.target.value)} style={taxSubInput} />
                                  </label>
                                  <label style={taxSubLabel}>Tax w/o abatement
                                    <input type="number" value={editData.tax_abatement_without ?? ""} onChange={(e) => updateField("tax_abatement_without", e.target.value)} style={taxSubInput} />
                                  </label>
                                  <label style={taxSubLabel}>Abatement details
                                    <input value={editData.tax_abatement_description ?? ""} onChange={(e) => updateField("tax_abatement_description", e.target.value)} style={{ ...taxSubInput, width: "180px" }} />
                                  </label>
                                </>
                              )}
                              <label style={taxSubLabel}>Boro
                                <input value={editData.borough ?? ""} onChange={(e) => updateField("borough", e.target.value)} style={{ ...taxSubInput, width: "70px" }} />
                              </label>
                              <label style={taxSubLabel}>Block
                                <input value={editData.block ?? ""} onChange={(e) => updateField("block", e.target.value)} style={{ ...taxSubInput, width: "80px" }} />
                              </label>
                              <label style={taxSubLabel}>Lot
                                <input value={editData.lot ?? ""} onChange={(e) => updateField("lot", e.target.value)} style={{ ...taxSubInput, width: "80px" }} />
                              </label>
                              {l.acris_url && (
                                <a href={l.acris_url} target="_blank" rel="noreferrer" style={{ fontSize: "12px", color: "#1565c0", fontWeight: 600, paddingBottom: "4px" }}>ACRIS ↗</a>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); alert("Get Tax — automation coming next (enqueue + poll, or ScrapingBee). For now fill the fields here."); }}
                                style={{ fontSize: "11px", fontWeight: 700, padding: "6px 12px", borderRadius: 4, background: "#2e7d32", color: "#fff", border: "none", cursor: "pointer" }}
                                title="Auto-fetch tax from NYC DOF (needs Block+Lot)"
                              >Get Tax</button>
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px" }}>
              <button onClick={() => setPage(1)} disabled={page === 1}
                style={{ ...paginationBtnStyle, opacity: page === 1 ? 0.5 : 1, cursor: page === 1 ? "not-allowed" : "pointer" }}>
                First
              </button>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                style={{ ...paginationBtnStyle, opacity: page === 1 ? 0.5 : 1, cursor: page === 1 ? "not-allowed" : "pointer" }}>
                Previous
              </button>
              <span style={{ color: "#666" }}>Page {page} of {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                style={{ ...paginationBtnStyle, opacity: page === totalPages ? 0.5 : 1, cursor: page === totalPages ? "not-allowed" : "pointer" }}>
                Next
              </button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                style={{ ...paginationBtnStyle, opacity: page === totalPages ? 0.5 : 1, cursor: page === totalPages ? "not-allowed" : "pointer" }}>
                Last
              </button>
              <select value={limit} onChange={(e) => { setLimit(parseInt(e.target.value)); setPage(1); }}
                style={{ padding: "8px 12px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px", backgroundColor: "#fff" }}>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
                <option value={200}>200 / page</option>
              </select>
            </div>
          </>
        )}
      </div>
      {/* Edit Modal */}
      <ListingEditModal
        listing={editModalListing}
        onClose={() => setEditModalListing(null)}
        onSave={(listingId, formData, detailsPayload) => {
          setListings((prev) =>
            prev.map((l) => {
              if (l.id !== listingId) return l;
              return {
                ...l,
                unit_number: formData.unit_number || null,
                floor: formData.floor || null,
                bedrooms: formData.bedrooms,
                bathrooms: formData.bathrooms,
                sqft: formData.sqft ? parseInt(formData.sqft) : null,
                list_price: formData.list_price ? parseFloat(formData.list_price) : null,
                listing_status: formData.listing_status,
                created_date: formData.created_date || l.created_date,
                dishwasher: formData.dishwasher,
                washer_dryer: formData.washer_dryer,
                renovation_level: formData.renovation_level,
                premium_views: formData.premium_views,
                hoa: formData.hoa ? parseFloat(formData.hoa) : null,
                tax: formData.tax ? parseFloat(formData.tax) : null,
                floor_line: formData.floor_line || null,
                listing_url: formData.listing_url || null,
                block: formData.block || null,
                lot: formData.lot || null,
                borough: formData.borough || null,
                acris_url: formData.acris_url || null,
                tax_abatement: formData.tax_abatement,
                tax_abatement_description: formData.tax_abatement_description || null,
                furnished: formData.furnished,
                deeded_parking: formData.deeded_parking,
                listing_details: { ...(l.listing_details || {}), ...(detailsPayload || {}) },
              };
            })
          );
          setEditModalListing(null);
        }}
        onVerify={(listingId) => {
          setListings((prev) => prev.filter((l) => l.id !== listingId));
          setTotal((prev) => prev - 1);
          setEditModalListing(null);
        }}
      />
      {/* Image Review Modal */}
      {imageReviewListing && (
        <ImageReviewModal
          listing={imageReviewListing}
          onClose={() => setImageReviewListing(null)}
          onSaved={() => loadListings()}
          onNext={() => {
            const allListings = listings.flatMap(u => u.listings || [u]);
            const idx = allListings.findIndex(l => l.id === imageReviewListing.id);
            if (idx >= 0 && idx < allListings.length - 1) setImageReviewListing(allListings[idx + 1]);
          }}
          onPrev={() => {
            const allListings = listings.flatMap(u => u.listings || [u]);
            const idx = allListings.findIndex(l => l.id === imageReviewListing.id);
            if (idx > 0) setImageReviewListing(allListings[idx - 1]);
          }}
        />
      )}
      {/* Building Detail Panel */}
      {buildingDetail && (
        <BuildingDetailPanel
          building={buildingDetail}
          loading={buildingDetailLoading}
          onClose={() => setBuildingDetail(null)}
          onFeedbackAdded={() => loadBuildingDetail(buildingDetail.id)}
        />
      )}
      {/* Merge Modal */}
      {mergeListingId && (
        <ListingMergeModal
          key={mergeListingId}
          listingId={mergeListingId}
          onClose={() => setMergeListingId(null)}
          onMerged={() => { setMergeListingId(null); loadListings(); }}
        />
      )}
    </>
  );
}

// Styles
const thStyle = {
  textAlign: "left",
  padding: "10px 12px",
  fontWeight: "600",
  fontSize: "12px",
  color: "#333",
  borderBottom: "2px solid #e0e0e0",
  whiteSpace: "nowrap",
};

// Inline tax sub-form (revealed under a sale row while editing)
const taxSubLabel = {
  display: "flex", flexDirection: "column", gap: "3px",
  fontSize: "10px", fontWeight: 600, color: "#777",
};
const taxSubInput = {
  padding: "4px 6px", fontSize: "12px", border: "1px solid #ccc", borderRadius: "4px", width: "110px",
};

const tdStyle = {
  padding: "10px 12px",
  whiteSpace: "nowrap",
};

const editTdStyle = {
  padding: "4px 6px",
  whiteSpace: "nowrap",
};

const inputStyle = {
  padding: "4px 6px",
  border: "1px solid #bbb",
  borderRadius: "4px",
  fontSize: "12px",
  width: "80px",
  backgroundColor: "#fff",
};

const selectStyle = {
  padding: "4px 4px",
  border: "1px solid #bbb",
  borderRadius: "4px",
  fontSize: "12px",
  backgroundColor: "#fff",
  cursor: "pointer",
};

const actionBtnStyle = {
  padding: "3px 8px",
  border: "1px solid #ccc",
  borderRadius: "4px",
  fontSize: "11px",
  cursor: "pointer",
  fontWeight: "500",
};

const paginationBtnStyle = {
  padding: "8px 16px",
  backgroundColor: "#f5f5f5",
  border: "1px solid #ddd",
  borderRadius: "6px",
  fontSize: "14px",
};

function SortableHeader({ label, field, sortField, sortDirection, onSort }) {
  const isActive = sortField === field;
  return (
    <th style={{ ...thStyle, cursor: "pointer", userSelect: "none" }} onClick={() => onSort(field)}>
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {label}
        <span style={{ fontSize: "10px", color: isActive ? "#1976d2" : "#999" }}>
          {isActive ? (sortDirection === "asc" ? "▲" : "▼") : "▲▼"}
        </span>
      </div>
    </th>
  );
}
